/*
 * Fila de escrita do carrinho — serializa TODAS as mutações que o tema
 * dispara, uma em voo por vez.
 *
 * Motivo (bug reproduzido em 08/09/2026, ver CLAUDE.md item 11): o Dawn
 * dispara `/cart/change` sem nenhum controle de concorrência. Duas mutações
 * válidas em linhas diferentes, com as respostas chegando fora de ordem,
 * faziam a resposta ANTIGA re-renderizar as sections por último e a UI
 * regredia para um estado que o servidor já não tinha — sem se corrigir
 * sozinha. Medido: servidor em qty [2,3]/$489.85/5 itens, DOM exibindo
 * [2,1]/3 itens; no drawer o total exibido também regredia
 * ($489.85 -> $293.91).
 *
 * Por que serializar em vez de descartar resposta obsoleta: descartar por
 * ordem de EMISSÃO só é correto se a ordem de emissão for a mesma que a ordem
 * de processamento no servidor — e não é, duas requisições em voo podem chegar
 * em qualquer ordem. Com uma escrita por vez a ordem do servidor passa a ser a
 * ordem da cliente, e a última resposta é sempre a autoritativa. (Em
 * `assets/ws-cart-incentives.js` a guarda de sequência é suficiente porque lá
 * não há mutação: é só leitura de uma section.)
 *
 * Fila ÚNICA e compartilhada, não uma por componente: cart page, cart drawer e
 * o formulário de produto (quick add do `ws-cart-set-suggestion` na própria
 * `/cart`, quick add da coleção, PDP) escrevem no MESMO carrinho na mesma
 * página.
 *
 * Fora do alcance desta fila (documentado, não corrigido aqui):
 * - `cart-note` (`/cart/update` com `note`) — não re-renderiza section
 *   nenhuma, então não produz divergência de DOM mesmo intercalado.
 * - `quick-order-list.js` / `quick-add-bulk.js` — não são renderizados neste
 *   tema (`quick_add` é "standard", não "bulk").
 * - Requisições de apps: fora do alcance do tema por definição. Se um app
 *   escrever no carrinho em paralelo, esta fila não o serializa.
 */
(function () {
  if (window.wsCartQueue) return;

  // Cadeia de promises: cada tarefa só começa depois que a anterior SETTLE
  // (sucesso ou falha). O catch interno garante que uma falha nunca trave a
  // fila — sem isso um erro deixaria todos os controles bloqueados.
  var chain = Promise.resolve();
  var inFlight = 0;
  // Tarefas enfileiradas e ainda não terminadas, contadas desde o enqueue.
  // `inFlight` só sobe quando a tarefa começa: uma tarefa enfileirada DURANTE
  // outra (ex.: a remoção automática do brinde, decidida no meio do "−")
  // ficava invisível no intervalo entre as duas, e o checkout retido era
  // liberado antes dela (achado na revisão de 28/10/2026).
  var pending = 0;

  // Intenções enfileiradas mas ainda NÃO iniciadas, por line key. Guardadas
  // pra (1) consolidar cliques repetidos na mesma linha e (2) reaplicar a
  // quantidade desejada nos inputs depois de cada re-render.
  var queued = new Map();

  var listeners = [];
  var checkoutIntent = null;

  function state() {
    return { busy: inFlight > 0, queued: queued.size };
  }

  function hasWork() {
    return inFlight > 0 || pending > 0 || queued.size > 0;
  }

  function notify() {
    var snapshot = state();
    listeners.forEach(function (fn) {
      try {
        fn(snapshot);
      } catch (e) {
        /* um listener quebrado não pode derrubar a fila */
      }
    });
  }

  function escapeKey(key) {
    if (window.CSS && CSS.escape) return CSS.escape(key);
    return String(key).replace(/["\\]/g, '\\$&');
  }

  function inputsForKey(key, root) {
    if (!key) return [];
    var scope = root || document;
    return Array.prototype.slice.call(
      scope.querySelectorAll('[data-quantity-line-key="' + escapeKey(key) + '"]')
    );
  }

  /*
   * Índice da linha (1-based) a partir da line key, lido do DOM recém
   * re-renderizado. É isso que impede uma operação enfileirada de acertar OUTRO
   * produto quando uma remoção anterior desloca os índices.
   *
   * A busca é ESCOPADA ao componente que originou a operação, resolvido ao vivo
   * (`cart-items` ou `cart-drawer-items`). Buscar no documento inteiro dá
   * índice errado: na página `/cart` o drawer também está no DOM com uma cópia
   * das mesmas linhas, e `CartItems.getSectionsToRender()` não re-renderiza o
   * drawer — a cópia dele fica com os índices ANTIGOS. Um bug real disso:
   * remover a linha 1 e, com a mudança pendente na linha 2, a operação era
   * enviada como `line: 2` (índice do drawer desatualizado) quando só restava
   * 1 linha; a Shopify ignora índice fora do intervalo em silêncio e a
   * intenção da cliente se perdia. Resolver ao vivo também cobre o drawer, cujo
   * `<cart-drawer-items>` é SUBSTITUÍDO no re-render (o `this` de quem
   * enfileirou fica órfão).
   */
  function resolveLineFromDom(key, rootSelector) {
    var root = rootSelector ? document.querySelector(rootSelector) : null;
    if (rootSelector && !root) return null;
    var inputs = inputsForKey(key, root);
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].dataset.index) return inputs[i].dataset.index;
    }
    return null;
  }

  // Reconciliação com o estado real da Shopify. Complementar, não substituta do
  // controle de concorrência: só é consultada quando o DOM não resolve a linha
  // (render intermediário, drawer fechado) ou depois de uma falha de resultado
  // incerto. `routes.cart_url` mantém compatibilidade com locale/Markets.
  function fetchCartState() {
    return fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function resolveLineFromServer(key) {
    return fetchCartState().then(function (cart) {
      if (!cart || !cart.items) return null;
      for (var i = 0; i < cart.items.length; i++) {
        // item.key identifica a linha incluindo properties e selling plan —
        // duas linhas do mesmo variant com properties diferentes têm keys
        // diferentes, então isso não as confunde.
        if (cart.items[i].key === key) return String(i + 1);
      }
      return null;
    });
  }

  // Depois de cada render, o input volta com o valor do servidor. Se a cliente
  // já pediu outra quantidade que ainda está na fila, o valor do servidor é
  // ANTIGO em relação à intenção dela — reaplicar evita tanto o "pisca e
  // volta" quanto o +/- seguinte calcular em cima da base errada (os botões
  // fazem stepUp/stepDown em cima do valor atual do input).
  function reapplyQueuedQuantities() {
    queued.forEach(function (entry, key) {
      inputsForKey(key).forEach(function (input) {
        var desired = String(entry.quantity);
        if (input.value !== desired) input.value = desired;
      });
    });
  }

  function enqueue(run) {
    pending++;
    var task = chain
      .then(function () {
        inFlight++;
        notify();
        return run();
      })
      .catch(function (error) {
        // Falha já tratada por quem enfileirou; aqui só impede que a cadeia
        // rejeite e trave as próximas escritas.
        if (error) console.error('ws-cart-queue:', error);
      })
      .then(function () {
        inFlight--;
        pending--;
        reapplyQueuedQuantities();
        notify();
        maybeResumeCheckout();
      });

    chain = task;
    return task;
  }

  /*
   * Enfileira uma mudança de quantidade de linha.
   *
   * intent: { lineKey, line, quantity }  — `quantity` é sempre ABSOLUTA (é o
   *   valor final do input; os botões +/- já resolvem incremento/decremento
   *   antes de disparar o change), então consolidar é seguro: a última
   *   intenção substitui a anterior sem perder cliques.
   * run(resolvedLine, quantity) — executa a mutação de fato.
   * onSkip(reason) — a linha deixou de existir; NÃO redirecionar pra outra
   *   linha nem recriar o produto.
   */
  function enqueueLineUpdate(intent, run, onSkip) {
    var key = intent.lineKey;

    if (key && queued.has(key)) {
      var existing = queued.get(key);
      existing.quantity = intent.quantity;
      existing.run = run;
      existing.onSkip = onSkip;
      notify();
      return existing.promise;
    }

    var entry = {
      quantity: intent.quantity,
      fallbackLine: intent.line,
      run: run,
      onSkip: onSkip,
    };
    if (key) queued.set(key, entry);
    notify();

    entry.promise = enqueue(function () {
      if (key) queued.delete(key);

      if (!key) return entry.run(entry.fallbackLine, entry.quantity);

      var line = resolveLineFromDom(key, intent.rootSelector);
      if (line !== null) return entry.run(line, entry.quantity);

      return resolveLineFromServer(key).then(function (serverLine) {
        if (serverLine === null) {
          if (entry.onSkip) entry.onSkip('line-gone');
          return;
        }
        return entry.run(serverLine, entry.quantity);
      });
    });

    return entry.promise;
  }

  /*
   * Checkout não pode ultrapassar intenção ainda não enviada: se houver
   * escrita em voo ou na fila, o clique é retido (não descartado) e refeito
   * assim que a fila esvazia. Listener em captura pra rodar antes do submit
   * nativo do form, sem editar os templates de checkout.
   */
  // Live region PRÓPRIA. As do Dawn (`cart-live-region-text` /
  // `CartDrawer-LiveRegionText`) entram em `getSectionsToRender()` e são
  // reescritas a cada re-render — a mensagem sumia antes de ser anunciada
  // (confirmado em teste: textContent vazio logo depois de anunciar). Esta fica
  // fora das sections, no <body>, então sobrevive.
  var liveRegion = null;

  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = message;
  }

  function maybeResumeCheckout() {
    if (!checkoutIntent || hasWork()) return;
    var button = checkoutIntent;
    checkoutIntent = null;
    // No drawer, o redesenho troca o `.drawer__inner` inteiro, botão de
    // checkout incluído: o botão retido ficava fora da página, `button.form`
    // vinha null e o clique se perdia (bug de 09/09/2026, achado na revisão
    // de 28/10/2026). Usa o botão novo, com o mesmo id.
    if (!button.isConnected && button.id) {
      button = document.getElementById(button.id) || button;
    }
    button.removeAttribute('aria-busy');
    announce('');
    var form = button.form;
    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit(button);
    } else {
      button.click();
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest && event.target.closest('[name="checkout"]');
      if (!button) return;
      // Idle e sem intenção retida: deixa passar, comportamento nativo.
      if (!hasWork() && !checkoutIntent) return;

      // Ocupado (ou já com uma intenção retida): SEMPRE engole o clique.
      // Bug corrigido em 09/09/2026: antes o `return` cedo em cima só
      // verificava `checkoutIntent` truthy antes de decidir interceptar —
      // então um 2º/3º clique enquanto já ocupado (com o 1º clique já retido)
      // não era interceptado e caía direto no submit nativo do form,
      // navegando pro checkout com mutação ainda pendente. Reproduzido com
      // Playwright: 3 cliques rápidos com a fila ocupada geravam 2 submits
      // imediatos (deveria ser 0) + 1 replay depois (deveria ser exatamente
      // 1, total 3). Fix: interceptar sempre que ocupado/retido, e só pular
      // o re-armamento (`checkoutIntent = button`) se já havia um retido.
      event.preventDefault();
      event.stopPropagation();
      if (checkoutIntent) return;

      checkoutIntent = button;
      button.setAttribute('aria-busy', 'true');
      // WS: texto em inglês hardcoded — P3 de internacionalização, decisão
      // consciente, não descuido. Existe um mecanismo seguro pra strings de JS
      // core (`window.cartStrings`, populado por um <script> síncrono no <head>
      // de layout/theme.liquid a partir de locales/en.default.json — mesmo que
      // cart.js usa pra cartStrings.error/quantityError). Testado: adicionar UMA
      // chave só em en.default.json aciona `MatchingTranslations` do Theme Check
      // nos outros 30 arquivos de locale (regressão real contra o baseline do
      // item 11) — precisaria traduzir a chave nos 30 pra não regredir, custo
      // desproporcional a uma frase que só aparece durante uma janela curta de
      // "carrinho ocupado". Mantido hardcoded até haver justificativa pra tocar
      // os 30 arquivos de locale.
      announce('Updating your cart before checkout…');
    },
    true
  );

  window.wsCartQueue = {
    enqueue: enqueue,
    enqueueLineUpdate: enqueueLineUpdate,
    fetchCartState: fetchCartState,
    hasWork: hasWork,
    state: state,
    onChange: function (fn) {
      listeners.push(fn);
      return function () {
        listeners = listeners.filter(function (item) {
          return item !== fn;
        });
      };
    },
  };
})();
